import React from "react";
import { Label } from "components";
import { useExemptionsContext } from "../../ExemptionsContext";
import ExemptionsAgreementPageEstimateCard from "../ExemptionsAgreementPageEstimateCard";
import { useResponsiveContext } from "context/ResponsiveContext";
import SignatureBox from "elements/SignatureBox";
import { useState } from "react";
import Checkmark from "public/account/exemptions/green-checkmark.svg";
import { usePropertyContext } from "../../context";
import client, { createUrl } from "utils/client";
import { sendErrorToWebhook } from "utils/sendWebhook";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { useAuthContext } from "context/AuthContext";
import concatAddressLine2 from "utils/concatAddressLine2";
import ButtonV2 from "elements/ButtonV2";

const Agreement = () => {
	const {
		flowId,
		next,
		additionalOwnerFirstNameValue,
		additionalOwnerLastNameValue,
		ownerDlFilesToUpload,
		owner2DlFilesToUpload,
		ownerDlExemptValue,
		owner2DlExemptValue,
		purchaseDateValue,
		moveInDateValue,
		isEligibleForRetro,
		setExemptionId,
		ownersValue,
	} = useExemptionsContext();
	const { userData } = useAuthContext();
	const { details } = usePropertyContext();
	const { isMobile } = useResponsiveContext(); // TODO: Convert this to CSS only in the future
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState(false);
	const [signature, setSignature] = useState("");
	const [signature2, setSignature2] = useState("");
	const submitSignatureRef = React.useRef<() => void | null>(null);
	const submitSignature2Ref = React.useRef<() => void | null>(null);
	const [signatureDisabled, setSignatureDisabled] = useState(true);
	const [signature2Disabled, setSignature2Disabled] = useState(true);

	const trackEvent = useEventTracker();

	const agreementUrl = React.useMemo(() => {
		if (userData && details) {
			const { first_name, last_name, phone, email } = userData;
			const {
				address,
				address2,
				city,
				state,
				zip_code,
				county,
				apn,
				raw_apn,
				contingency,
			} = details;
			const searchParams = new URLSearchParams();
			searchParams.append("address", concatAddressLine2(address, address2));
			searchParams.append("city", city);
			searchParams.append("state", state);
			searchParams.append("zipcode", zip_code ?? "");
			searchParams.append("phone", phone ?? "");
			searchParams.append("email", email ?? "");
			searchParams.append("county", county!);
			searchParams.append("apn", raw_apn ?? apn ?? "");
			searchParams.append("rate", contingency.toString());

			searchParams.append("name", `${first_name} ${last_name}`);

			return (
				createUrl("/pdf/Authorization-Agreement?") + searchParams.toString()
			);
		}
		return null;
	}, [userData, details]);

	const showSecondSignature = React.useMemo(() => {
		const geoRequiresBothSignatures = [
			"TEXAS_EXEMPTION",
			"FLORIDA_EXEMPTION",
			"MICHIGAN_EXEMPTION",
			"CALIFORNIA_EXEMPTION",
		].includes(flowId ?? "");

		if (geoRequiresBothSignatures && ownersValue !== "Sole Owner") {
			return true;
		}
		return false;
	}, [flowId, details, ownersValue]);

	const handleSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
		e.preventDefault();
		if (!details) return;
		setError(false);
		setSubmitting(true);
		try {
			const { data } = await client.submitExemptionsSignature({
				propertyId: details?.id,
				signature: signature,
				signature2: signature2,
			});
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Legal_Page_Answer,
				data: {
					"Signed Agreement": true,
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
				},
			});
			const trackingEventData = {
				"Signed Agreement": true,
			};

			if (data.exemption_id) {
				setExemptionId(data.exemption_id);
			}

			next({ trackingEventData });
		} catch (e) {
			console.error(
				"Error on Exemptions Signature page - unable to submit user signature: " +
					e
			);
			sendErrorToWebhook(
				"** Error on Exemptions Signature page - unable to submit user signature ** " +
					e
			);
			setError(true);
		} finally {
			setSubmitting(false);
		}
	};

	const showDriversLicenseUploadStatus = React.useMemo(() => {
		if (flowId === "TEXAS_EXEMPTION") return true;
		if (flowId === "ILLINOIS_EXEMPTION") return true;
		if (flowId === "FLORIDA_EXEMPTION") return true;
		if (
			flowId === "GEORGIA_EXEMPTION" &&
			["Fulton", "DeKalb"].includes(details?.county ?? "")
		) {
			return true;
		}

		if (flowId === "MICHIGAN_EXEMPTION") {
			return true;
		}

		return false;
	}, [flowId, details]);

	const show2ndDriversLicenseUploadStatus = React.useMemo(() => {
		return (
			(flowId === "TEXAS_EXEMPTION" &&
				!!details?.exemption_requires_both_licenses) ||
			(flowId === "GEORGIA_EXEMPTION" &&
				["Fulton", "DeKalb"].includes(details?.county ?? "")) ||
			flowId === "FLORIDA_EXEMPTION" ||
			flowId === "MICHIGAN_EXEMPTION"
		);
	}, [flowId, details]);

	const submitDisabled = React.useMemo(() => {
		if (showSecondSignature) {
			return signatureDisabled || signature2Disabled;
		}
		return signatureDisabled;
	}, [showSecondSignature, signatureDisabled, signature2Disabled]);

	return (
		<div>
			{[
				"ILLINOIS_EXEMPTION",
				"TEXAS_EXEMPTION",
				"NEW_YORK_EXEMPTION",
				"FLORIDA_EXEMPTION",
				"MICHIGAN_EXEMPTION",
				"PENNSYLVANIA_EXEMPTION",
			].includes(flowId ?? "") && (
				<ExemptionsAgreementPageEstimateCard
					isRetroEligible={isEligibleForRetro}
					horizontal={!isMobile}
				/>
			)}
			<div className="exemptions-question-card mt-4 ">
				<p className="bold">Review details and submit for filing</p>
				<div className="exemptions-survey-agreement-review-details-inner-container">
					<div className="exemptions-question-card-white-card mt-2 mb-2">
						<p className="bold sm">Owner Info</p>
						<div className="exemptions-survey-agreement-info">
							<Label className="exemptions-survey-agreement-info-owner1-label">
								Owner #1
							</Label>
							<p className="sm exemptions-survey-agreement-info-right-align exemptions-survey-agreement-info-owner no-translate">
								{details?.auth_first_name && details.auth_last_name
									? details.auth_first_name + " " + details.auth_last_name
									: (details?.auth_company ?? "N/A")}
							</p>
						</div>
						{showDriversLicenseUploadStatus && (
							<div className="exemptions-survey-agreement-info exemptions-survey-agreement-info-drivers-license">
								<Label>Driver’s license</Label>
								{ownerDlFilesToUpload.length > 0 ||
								details?.exemption?.owner1_license_ref ? (
									<div className="exemptions-survey-agreement-info-uploaded">
										<p className="sm">Uploaded</p>
										<Checkmark />
									</div>
								) : ownerDlExemptValue ? (
									<p className="sm">N/A</p>
								) : (
									<p className="sm terracotta">Upload Later</p>
								)}
							</div>
						)}

						{!!additionalOwnerFirstNameValue && (
							<>
								<div className="exemptions-survey-agreement-info">
									<Label className="exemptions-survey-agreement-info-owner2-label">
										Owner #2
									</Label>
									<p className="sm exemptions-survey-agreement-info-right-align exemptions-survey-agreement-info-owner no-translate">
										{additionalOwnerFirstNameValue &&
										additionalOwnerLastNameValue
											? additionalOwnerFirstNameValue +
												" " +
												additionalOwnerLastNameValue
											: "N/A"}
									</p>
								</div>
								{show2ndDriversLicenseUploadStatus && (
									<div className="exemptions-survey-agreement-info exemptions-survey-agreement-info-drivers-license">
										<Label>Driver’s license</Label>
										{owner2DlFilesToUpload.length > 0 ||
										details?.exemption?.owner2_license_ref ? (
											<div className="exemptions-survey-agreement-info-uploaded">
												<p className="sm">Uploaded</p>
												<Checkmark />
											</div>
										) : owner2DlExemptValue ? (
											<p className="sm">N/A</p>
										) : (
											<p className="sm terracotta">Upload Later</p>
										)}
									</div>
								)}
							</>
						)}
					</div>
					<div className="exemptions-question-card-white-card mt-2 mb-2">
						<p className="bold sm">Property Info</p>
						<div className="exemptions-survey-agreement-info">
							<Label>Street address</Label>
							<p className="sm exemptions-survey-agreement-info-right-align no-translate">
								{details?.address +
									(details?.address2 ? " " + details.address2 : "")}
							</p>
						</div>
						{!!purchaseDateValue && purchaseDateValue !== "0000-00-00" && (
							<div className="exemptions-survey-agreement-info">
								<Label>Purchase Date</Label>
								<p className="sm exemptions-survey-agreement-info-right-align no-translate">
									{purchaseDateValue === "0000-00-00" || !purchaseDateValue
										? "N/A"
										: purchaseDateValue}
								</p>
							</div>
						)}
						{!["NEW_YORK_EXEMPTION", "PENNSYLVANIA_EXEMPTION"].includes(
							flowId ?? ""
						) && (
							<div className="exemptions-survey-agreement-info">
								<Label>Move In Date</Label>
								<p className="sm exemptions-survey-agreement-info-right-align no-translate">
									{moveInDateValue === "0000-00-00" || !moveInDateValue
										? "N/A"
										: moveInDateValue}
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
			<div className="exemptions-survey-legal-terms">
				<p className="sm bold">
					By signing I certify, under penalty of perjury that:
				</p>
				{flowId === "TEXAS_EXEMPTION" && (
					<ul>
						<li className="sm">Each fact provided is true and correct.</li>
						<li className="sm">
							I meet the qualification for homestead exemption.
						</li>
						<li className="sm">
							I do not claim an exemption on another residence homestead.
						</li>
						{isEligibleForRetro && (
							<li className="sm">
								I also agree to the 25% contingency fee if a refund is
								successful, with a Savings-or-Free Guarantee.
							</li>
						)}
					</ul>
				)}
				{flowId === "CALIFORNIA_EXEMPTION" && (
					<ul>
						<li className="sm">
							I have reviewed and agree to the attached service agreement.
						</li>
						<li className="sm">
							Each fact provided to complete the form is true and correct.
						</li>
						<li className="sm">
							I do not claim a primary residence exemption on another property.
						</li>
						<li className="sm">
							I agree to the Savings-or-Free Guarantee terms: to pay Ownwell
							$18.37 which is roughly ~35% of first year tax savings if the
							exemption is successfully applied.
						</li>
					</ul>
				)}
				{flowId === "GEORGIA_EXEMPTION" && (
					<ul>
						<li className="sm">
							I have reviewed and agree to the attached service agreement.
						</li>
						<li className="sm">
							Each fact provided to complete the form is true and correct.
						</li>
						<li className="sm">
							I do not claim a primary residence exemption on another property.
						</li>
						<li className="sm">
							I agree to the Savings-or-Free Guarantee terms: to pay Ownwell ~
							{details?.contingency ?? "25"}% of first year tax savings equal to
							$125.
						</li>
					</ul>
				)}
				{flowId === "NEW_YORK_EXEMPTION" && (
					<ul>
						<li className="sm">Each fact provided is true and correct.</li>
						<li className="sm">
							I meet the qualification for the STAR Credit and,
						</li>
						<li className="sm">
							I do not claim an exemption or STAR Credit on another residence.
						</li>
						{isEligibleForRetro && (
							<li className="sm">
								I also agree to the 35% contingency fee if a refund is
								successful, with a Savings-or-Free Guarantee.
							</li>
						)}
					</ul>
				)}
				{flowId === "ILLINOIS_EXEMPTION" && (
					<ul>
						<li className="sm">Each fact provided is true and correct.</li>
						<li className="sm">
							I meet the qualification for the homestead exemption and,
						</li>
						<li className="sm">
							I do not claim an exemption on another residence homestead.
						</li>
						{isEligibleForRetro && (
							<li className="sm">
								I also agree to the 25% contingency fee if a refund is
								successful, with a Savings-or-Free Guarantee.
							</li>
						)}
					</ul>
				)}
				{flowId === "FLORIDA_EXEMPTION" && (
					<ul>
						<li className="sm">Each fact provided is true and correct.</li>
						<li className="sm">
							I meet the qualification for the homestead exemption and,
						</li>
						<li className="sm">
							I do not claim an exemption on another residence homestead.
						</li>
						<li className="sm">
							I agree to the Savings-or-Free Guarantee terms: to pay Ownwell
							$260 which is roughly ~{details?.contingency ?? 25}% of first year
							tax savings. If there are no savings, you will not be charged.
						</li>
					</ul>
				)}
				{flowId === "MICHIGAN_EXEMPTION" && (
					<ul>
						<li className="sm">Each fact provided is true and correct.</li>
						<li className="sm">
							I meet the qualification for an exemption, and
						</li>
						<li className="sm">
							I do not claim an exemption on another residence.
						</li>
						{isEligibleForRetro && (
							<li className="sm">
								I also agree to the 35% contingency fee if a refund is
								successful, with a Savings-or-Free Guarantee. If there are no
								savings, you will not be charged.
							</li>
						)}
					</ul>
				)}
				{flowId === "PENNSYLVANIA_EXEMPTION" && (
					<ul>
						<li className="sm">Each fact provided is true and correct.</li>
						<li className="sm">
							I meet the qualification for an exemption, and
						</li>
						<li className="sm">
							I do not claim an exemption on another residence.
						</li>
						<li className="sm">
							I agree to the Savings-or-Free Guarantee terms: to pay Ownwell
							$185 which is roughly ~35% of first year tax savings. If there are
							no savings, you will not be charged.
						</li>
					</ul>
				)}
			</div>
			{error && (
				<p className="sm text-center terracotta mt-4">
					Whoops, something went wrong. Try again.
				</p>
			)}
			<form id="appeal-legal-signature-box-form" onSubmit={handleSubmit}>
				{agreementUrl && (
					<p id="appeal-legal-disclaimer" className="sm">
						By signing below, you indicate that you have read and agree to the
						terms of the{" "}
						<a href={agreementUrl} target="_blank" rel="noreferrer">
							Ownwell Service and Authorization Agreements
						</a>
						.
					</p>
				)}
				<SignatureBox
					ref={submitSignatureRef}
					showSubmitButton={false}
					submitting={submitting}
					canUseType={false}
					onSubmit={signature => {
						setSignature(signature);
					}}
					onDisabledChange={setSignatureDisabled}>
					<div className="signature-context-box">
						<Label>Signature of owner{showSecondSignature ? " #1" : ""}</Label>
						<p>
							{userData?.first_name} {userData?.last_name}
						</p>
					</div>
				</SignatureBox>
				{showSecondSignature && (
					<SignatureBox
						ref={submitSignature2Ref}
						showSubmitButton={false}
						submitting={submitting}
						canUseType={false}
						onSubmit={signature2 => {
							setSignature2(signature2);
						}}
						onDisabledChange={setSignature2Disabled}>
						<div className="signature-context-box">
							<Label>Signature of owner #2</Label>
							<p>
								{additionalOwnerFirstNameValue} {additionalOwnerLastNameValue}
							</p>
						</div>
					</SignatureBox>
				)}
				<ButtonV2
					className="mt-2 mx-auto"
					size="large"
					type="submit"
					onClick={() => {
						submitSignatureRef.current?.();
						if (showSecondSignature) {
							submitSignature2Ref.current?.();
						}
					}}
					disabled={submitDisabled}>
					{submitting ? "Submitting..." : "Sign Agreement"}
				</ButtonV2>
			</form>
		</div>
	);
};

export default Agreement;
